import React, { Fragment, useContext, useEffect, useState, type FC, type PropsWithChildren } from 'react';

import { Heading, Box, Text, LayoutContext, reactHTMLParser } from '@lucid/components';
import kebabCase from 'lodash/kebabCase';
import { useSmartlingStrings } from '@lucid/hooks';

import type { Lucid } from 'types/types';
import Person, { type PersonColor, type PersonGeneric } from './person';

export interface PeopleSectionProps extends Omit<Lucid.Directus.Generated.People_Section, 'people' | 'id'> {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Set to show the company name - otherwise hide it */
  willShowCompany?: boolean;
  /** Sets the color scheme for the people backgrounds */
  colorScheme?: PersonColor[];
  /** The available variants for the PeopleSection component. */
  variant?: Lucid.Theme.Component.PeopleSection.VariantKeys;
  /** Set to show a dark background */
  isDarkBackground?: boolean;
  /** Set to hide the bio */
  shouldHideBio?: boolean;
  /** The normalized people data to be rendered in the Person component */
  people: PersonGeneric[];
}

const BioDetail = ({ title, value }) => (
  <Box
    css={theme => ({
      borderLeft: `1px solid ${theme.colors.pink[40]}`,
      paddingLeft: theme.space.s16,
      [theme.breakpoints.bp900.min]: {
        paddingTop: theme.space.s16,
        paddingLeft: 0,
        borderLeft: 'none',
        borderTop: `1px solid ${theme.colors.pink[40]}`,
        maxWidth: '225px',
      },
    })}
  >
    <Heading as="h4" css={theme => ({ marginBottom: theme.space.s8, color: theme.colors.white })} variant="headingS">
      {title}
    </Heading>
    <Text
      as={value?.includes('<ul>') ? 'div' : 'p'}
      css={theme => [
        theme.shared.paragraphNestedLinkStyles(theme, 'secondaryInverse'),
        {
          a: { display: 'inline-flex', '&:hover': { fontWeight: theme.fontWeights.fw400 } },
          li: { marginBottom: theme.space.s8 },
        },
      ]}
    >
      {reactHTMLParser(value)}
    </Text>
  </Box>
);

export const normalizePople = (people?: Lucid.Directus.Generated.People_Section_Person[]) =>
  people?.map(({ person_id: person }) => ({
    ...person,
    name: person?.name,
    image: person?.image,
    company: person?.company,
    bio: person?.translations?.[0]?.bio,
    title: person?.translations?.[0]?.title,
  })) as PersonGeneric[];

const PeopleSection: FC<PropsWithChildren<PeopleSectionProps>> = ({
  className,
  heading,
  isDarkBackground,
  people,
  willShowCompany = false,
  shouldHideBio = false,
}) => {
  const { shortCode } = useContext(LayoutContext);
  const [expandedIndex, setExpandedIndex] = useState(-1);
  const [currentPerson, setCurrentPerson] = useState<PersonGeneric | null>(null);

  const withSmartling = useSmartlingStrings({
    keys: [
      'mktg_leadership_bio_year',
      'mktg_leadership_education',
      'mktg_leadership_fav_video',
      'mktg_leadership_show_more',
      'mktg_leadership_show_less',
    ],
    shortCode,
  });

  useEffect(() => {
    if (expandedIndex === -1 || !people) {
      setCurrentPerson(null);
    } else {
      const selectedPerson = people?.[expandedIndex];

      setCurrentPerson(selectedPerson);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedIndex]);

  type BioDetailType = {
    i18nKey: string;
    value: string;
  };
  const bioDetails = [
    currentPerson?.bioDetails,
    { i18nKey: 'mktg_leadership_bio_year', value: currentPerson?.start_year },
    { i18nKey: 'mktg_leadership_education', value: currentPerson?.education },
    { i18nKey: 'mktg_leadership_fav_video', value: currentPerson?.favorite_video },
  ].filter(bd => !!bd?.value) as BioDetailType[];
  const hasAdditionalData = !!bioDetails.length;

  return (
    <Box className={className}>
      <Box
        css={theme => ({
          margin: 'auto',
          marginBottom: theme.space.s64,
        })}
      >
        {heading && (
          <Heading
            as="h2"
            css={theme => ({ color: isDarkBackground ? theme.colors.white : theme.colors.coolGrey[100] })}
            variant="heading2XL"
          >
            {heading}
          </Heading>
        )}
      </Box>
      <ul
        css={theme => ({
          display: 'grid',
          listStyle: 'none',
          justifyContent: 'center',
          width: '100%',
          marginLeft: 0,
          gridTemplateColumns: 'repeat(1, 1fr)',
          columnGap: theme.space.s24,
          [theme.breakpoints.bp500.min]: {
            gridTemplateColumns: 'repeat(2, 1fr)',
          },
          [theme.breakpoints.bp900.min]: {
            gridTemplateColumns: 'repeat(4, 1fr)',
          },
        })}
      >
        {people?.map(
          (person, index) =>
            person && (
              <Fragment key={`person-${index}`}>
                <li
                  key={`person-${index}`}
                  css={theme => ({
                    margin: 0,
                    marginBottom: theme.space.s64,
                    gridColumn: '1 / 2',
                    gridRow: `${Math.floor(index) * 2 + 1} / ${Math.floor(index) * 2 + 2}`,
                    [theme.breakpoints.bp500.min]: {
                      gridColumn: `${(index % 2) + 1} / ${(index % 2) + 2}`,
                      gridRow: `${Math.floor(index / 2) * 2 + 1} / ${Math.floor(index / 2) * 2 + 2}`,
                    },
                    [theme.breakpoints.bp900.min]: {
                      gridColumn: `${(index % 4) + 1} / ${(index % 4) + 2}`,
                      gridRow: `${Math.floor(index / 4) * 2 + 1} / ${Math.floor(index / 4) * 2 + 2}`,
                    },
                  })}
                >
                  <Person
                    bioElementId={`#${kebabCase(person?.name?.toLowerCase())}-bio`}
                    expandedIndex={expandedIndex}
                    index={index}
                    isDarkBackground={isDarkBackground}
                    person={person}
                    setExpandedIndex={setExpandedIndex}
                    shouldHideBio={shouldHideBio}
                    willShowCompany={willShowCompany}
                  />
                </li>
                {expandedIndex === index && currentPerson && (currentPerson.bio || currentPerson.bioDetails) && (
                  <Box
                    aria-labelledby={`bio-tab-${currentPerson.id}`}
                    css={theme => ({
                      background: theme.colors.white,
                      width: '100%',
                      borderRadius: theme.radii.r16,
                      display: 'grid',
                      gridTemplateColumns: '1fr',
                      marginTop: `-${theme.space.s32}px`,
                      marginBottom: theme.space.s64,
                      border: `1px solid ${theme.colors.coolGrey[10]}`,
                      overflow: 'hidden',
                      gridColumn: '1 / 2',
                      boxShadow: '2px 2px 3px 0px #282C3333',
                      gridRow: `${Math.floor(expandedIndex) * 2 + 2} / ${Math.floor(expandedIndex) * 2 + 3}`,
                      [theme.breakpoints.bp500.min]: {
                        gridColumn: '1 / 3',
                        gridRow: `${Math.floor(expandedIndex / 2) * 2 + 2} / ${Math.floor(expandedIndex / 2) * 2 + 3}`,
                      },
                      [theme.breakpoints.bp900.min]: {
                        gridColumn: '1 / 5',
                        gridTemplateColumns: hasAdditionalData ? '1fr minmax(0, 430px)' : '1fr',
                        gridRow: `${Math.floor(expandedIndex / 4) * 2 + 2} / ${Math.floor(expandedIndex / 4) * 2 + 3}`,
                      },
                    })}
                    id={`${kebabCase(currentPerson?.name?.toLowerCase())}-bio`}
                  >
                    <Box css={theme => ({ padding: theme.space.s24 })}>
                      <Heading
                        as="h3"
                        css={theme => ({
                          color: isDarkBackground ? theme.colors.white : theme.colors.coolGrey[100],
                        })}
                        variant="headingM"
                      >
                        {currentPerson.name}
                      </Heading>
                      <Text css={theme => ({ color: theme.colors.coolGrey[70] })} variant="paragraphS400">
                        {currentPerson.title}
                      </Text>
                      {currentPerson.bio && (
                        <Text
                          css={theme => ({
                            marginBottom: theme.space.s8,
                            color: isDarkBackground ? theme.colors.white : theme.colors.coolGrey[100],
                          })}
                          variant="paragraphS400"
                        >
                          {currentPerson.bio}
                        </Text>
                      )}
                    </Box>
                    {hasAdditionalData && (
                      <Box
                        css={theme => ({
                          backgroundColor: theme.colors.coolGrey[100],
                          color: 'white',
                        })}
                      >
                        <Box
                          css={theme => ({
                            display: 'grid',
                            flexDirection: 'column',
                            gridTemplateColumns: '1fr',
                            padding: theme.space.s24,
                            width: 'fit-content',
                            minWidth: '225px',
                            height: '100%',
                            gap: theme.space.s16,
                            justifyContent: 'space-between',
                            [theme.breakpoints.bp500.min]: {
                              gridTemplateColumns: '1fr 1fr',
                              width: '100%',
                            },
                            [theme.breakpoints.bp900.min]: {
                              gridTemplateColumns: '1fr',
                              width: 'fit-content',
                            },
                          })}
                        >
                          {bioDetails.map(({ i18nKey, value }) => (
                            <BioDetail key={withSmartling(i18nKey)} title={withSmartling(i18nKey)} value={value} />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </Fragment>
            ),
        )}
      </ul>
    </Box>
  );
};

export { PeopleSection };
export default PeopleSection;
